import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ProductCard from '@/components/base/ProductCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getProductsByCategory } from '@/mocks/products';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;

  if (!category) {
    return <NotFound />;
  }

  const categoryProducts = getProductsByCategory(slug!);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 愽彩糖果`,
    description: category.description,
    url: `https://bocaicandy.com/category/${category.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '糖果分类', href: '/category/fruit-candy' },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="w-full lg:w-56 shrink-0">
                <h3 className="text-base font-semibold text-foreground-800 mb-4">糖果分类</h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`block px-4 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      {cat.name}
                      <span className="ml-2 text-xs text-foreground-400">({cat.count})</span>
                    </Link>
                  ))}
                </nav>
              </aside>

              <div className="flex-1">
                <div className="mb-6">
                  <div className="relative w-full aspect-[3/1] rounded-lg overflow-hidden mb-6">
                    <img
                      src={category.image}
                      alt={category.name}
                      title={category.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h1 className="text-2xl md:text-3xl font-heading mb-1">{category.name}</h1>
                      <p className="text-sm text-white/80">{category.description}</p>
                    </div>
                  </div>
                </div>

                {categoryProducts.length > 0 ? (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {categoryProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        id={product.id}
                        name={product.name}
                        price={product.price}
                        originalPrice={product.originalPrice}
                        image={product.image}
                        badge={product.badge}
                        category={product.category}
                        rating={product.rating}
                        reviews={product.reviews}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                      <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无糖果，敬请期待</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}